import {
  NEWS_PUBLISHER_IDS,
  NEWS_PUBLISHERS,
  type NewsPublisherId,
} from '../constants/newsPublishers';
import { dedupeNewsStories } from '../utils/newsDedupe';
import {
  compileNewsBlocklist,
  loadNewsPreferences,
  type NewsPreferences,
} from '../utils/newsPreferences';
import { postMatchesBlocklist } from '../utils/redditTitleFilter';
import type { NewsStory, RankedNewsStory } from '../utils/newsStory';
import { fetchPublisherFeed } from './newsPublisherRssService';
import { rankStoriesWithCursor } from './newsCursorRankService';
import { fetchSubredditHot } from './redditRssService';
import { filterRedditPosts, type RedditFeedPost } from '../utils/redditTitleFilter';
import type { CursorAgentProgressCallback } from './cursorAgentApi';

/** Max headlines sent to Cursor (keeps prompt small). */
const MAX_CANDIDATES_FOR_RANKING = 80;

export type AggregatedNewsFeed = {
  ranked: RankedNewsStory[];
  blocklistInvalidLines: string[];
  rankError?: string;
  /** True when order came from the AI ranker (not raw feed order). */
  aiRanked?: boolean;
};

export type FetchAggregatedNewsOptions = {
  onRankProgress?: CursorAgentProgressCallback;
  /** When true, reorder headlines with the AI agent. Default false — show feed order first. */
  rankWithAi?: boolean;
};

export type RankNewsStoriesResult = {
  ranked: RankedNewsStory[];
  rankError?: string;
};

export async function fetchNewsCandidates(
  prefs: NewsPreferences = loadNewsPreferences(),
  signal?: AbortSignal
): Promise<{ candidates: NewsStory[]; blocklistInvalidLines: string[] }> {
  const blocklist = compileNewsBlocklist(prefs);

  const redditLimit = Math.min(25, prefs.maxPostsPerSub + 5);
  const pubLimit = prefs.maxPerPublisher + 3;

  const [redditResults, publisherResults] = await Promise.all([
    Promise.all(
      prefs.subreddits.map(async subreddit => {
        try {
          const raw = await fetchSubredditHot(subreddit, redditLimit, signal);
          return filterRedditPosts(raw, blocklist.patterns).slice(0, prefs.maxPostsPerSub);
        } catch {
          return [] as RedditFeedPost[];
        }
      })
    ),
    Promise.all(
      NEWS_PUBLISHER_IDS.filter(id => prefs.publishersEnabled[id]).map(id =>
        fetchPublisherFeed(id, pubLimit, signal)
      )
    ),
  ]);

  const rawStories: NewsStory[] = [];

  for (const feed of publisherResults) {
    for (const s of feed.stories) {
      if (!storyPassesBlocklist(s, blocklist.patterns)) rawStories.push(s);
    }
  }

  for (let i = 0; i < redditResults.length; i++) {
    const posts = redditResults[i]!;
    const sub = prefs.subreddits[i] ?? 'news';
    for (let j = 0; j < posts.length; j++) {
      const p = posts[j]!;
      const story: NewsStory = {
        title: p.title,
        link: p.link,
        sourceId: `reddit:${sub}`,
        sourceLabel: `r/${sub}`,
        feedRank: j,
      };
      if (!storyPassesBlocklist(story, blocklist.patterns)) rawStories.push(story);
    }
  }

  const { stories: deduped } = dedupeNewsStories(rawStories);
  return {
    candidates: deduped.slice(0, MAX_CANDIDATES_FOR_RANKING),
    blocklistInvalidLines: blocklist.invalidLines,
  };
}

export async function rankNewsStories(
  stories: readonly NewsStory[],
  prefs: NewsPreferences = loadNewsPreferences(),
  options: {
    onRankProgress?: CursorAgentProgressCallback;
    signal?: AbortSignal;
  } = {}
): Promise<RankNewsStoriesResult> {
  if (options.signal?.aborted) return { ranked: [] };
  if (stories.length === 0) return { ranked: [] };

  try {
    const ranked = await rankStoriesWithCursor(
      stories,
      prefs,
      options.onRankProgress,
      options.signal
    );
    return { ranked };
  } catch (err) {
    return {
      ranked: stories.slice(0, prefs.maxRankedStories),
      rankError: (err as Error).message,
    };
  }
}

export async function fetchAggregatedNews(
  prefs: NewsPreferences = loadNewsPreferences(),
  signal?: AbortSignal,
  options: FetchAggregatedNewsOptions = {}
): Promise<AggregatedNewsFeed> {
  const { candidates, blocklistInvalidLines } = await fetchNewsCandidates(prefs, signal);

  if (signal?.aborted) {
    return { ranked: [], blocklistInvalidLines };
  }

  if (options.rankWithAi) {
    const rankOpts: {
      onRankProgress?: CursorAgentProgressCallback;
      signal?: AbortSignal;
    } = {};
    if (options.onRankProgress) rankOpts.onRankProgress = options.onRankProgress;
    if (signal) rankOpts.signal = signal;
    const { ranked, rankError } = await rankNewsStories(candidates, prefs, rankOpts);
    return {
      ranked,
      blocklistInvalidLines,
      aiRanked: !rankError,
      ...(rankError ? { rankError } : {}),
    };
  }

  return {
    ranked: candidates.slice(0, prefs.maxRankedStories),
    blocklistInvalidLines,
    aiRanked: false,
  };
}

function storyPassesBlocklist(
  story: Pick<NewsStory, 'title' | 'link'>,
  patterns: readonly RegExp[]
): boolean {
  return postMatchesBlocklist(story, patterns);
}

export function publisherHomeUrl(id: NewsPublisherId): string {
  return NEWS_PUBLISHERS[id].homepage;
}
